// Barcode label printing from item rows (legacy ItemListComponent labelModal).
// The label template is the SVG designed under Settings → Label design
// (`svg_string`, `label_height`, `print_dialog`). Placeholders are replaced and
// the barcode is drawn into the `#replace-barcode` group with JsBarcode.
import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack } from '@mui/material';
import JsBarcode from 'jsbarcode';

import { adminApi } from '../../api/client';
import type { CrudActionContext, CrudRecord } from '../../crud/types';
import { t } from '../../i18n';
import { ensureLocation, useSessionLocation } from '../../session/location';

type LabelSettings = { svg: string | null; height: number; width: number; printDialog: boolean; decimals: number };

let settingsPromise: Promise<LabelSettings> | null = null;

function loadLabelSettings(): Promise<LabelSettings> {
  if (!settingsPromise) {
    settingsPromise = (async () => {
      let settings: Record<string, unknown> = {};
      try {
        const response = await adminApi.get('settings');
        settings = (response.data?.settings ?? {}) as Record<string, unknown>;
      } catch {
        try {
          const response = await adminApi.get('settings/label');
          settings = (response.data ?? {}) as Record<string, unknown>;
        } catch {
          settings = {};
        }
      }
      return {
        svg: typeof settings.svg_string === 'string' && settings.svg_string.includes('<svg') ? settings.svg_string : null,
        height: Number(settings.label_height ?? 150) || 150,
        width: Number(settings.label_width ?? 300) || 300,
        printDialog: Number(settings.print_dialog) === 1,
        decimals: Number(settings.decimals ?? 2) || 2
      };
    })().catch((error) => {
      settingsPromise = null;
      throw error;
    });
  }
  return settingsPromise;
}

const FORMAT: Record<string, string> = { code128: 'CODE128', code39: 'CODE39', ean5: 'EAN5', ean8: 'EAN8', ean13: 'EAN13', upca: 'UPC', upce: 'UPCE' };

function escapeXml(value: string) {
  return value.replace(/[<>&"']/g, (chr) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;' })[chr] ?? chr);
}

function formatMoney(value: unknown, decimals: number) {
  const number = Number(value ?? 0);
  return (Number.isFinite(number) ? number : 0).toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function drawBarcode(code: string, symbology: string): { svg: SVGSVGElement; fallback: boolean } {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const options = { width: 1, height: 30, fontSize: 12, margin: 0, marginBottom: 4, displayValue: true };
  try {
    JsBarcode(svg, code, { ...options, format: FORMAT[symbology] ?? 'CODE128' });
    return { svg, fallback: false };
  } catch {
    const retry = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    JsBarcode(retry, code || '-', { ...options, format: 'CODE128' });
    return { svg: retry, fallback: true };
  }
}

function defaultTemplate(width: number, height: number) {
  return `<svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
<rect x="0.5" y="0.5" width="${width - 1}" height="${height - 1}" fill="#fff" stroke="#ccc" rx="4"/>
<text x="${width / 2}" y="20" text-anchor="middle" font-family="sans-serif" font-size="12">Moder POS Solution</text>
<text x="${width / 2}" y="40" text-anchor="middle" font-family="sans-serif" font-size="14" font-weight="bold">--- Item Name ---</text>
<text x="${width / 2}" y="58" text-anchor="middle" font-family="sans-serif" font-size="11">--- Other info for item. ---</text>
<text x="${width / 2}" y="76" text-anchor="middle" font-family="sans-serif" font-size="12">--- Price: 1,000.00 ---</text>
<g id="replace-barcode"></g>
</svg>`;
}

/** Build the printable label SVG for an item row. */
export function buildLabelSvg(row: CrudRecord, settings: LabelSettings, locationName: string) {
  const template = settings.svg ?? defaultTemplate(settings.width, settings.height);
  const priceSource = Array.isArray(row.location_stock) && row.location_stock[0] && (row.location_stock[0] as Record<string, unknown>).price ? (row.location_stock[0] as Record<string, unknown>).price : row.price;
  const filled = template
    .replace('Moder POS Solution', escapeXml(locationName))
    .replace('--- Item Name ---', escapeXml(String(row.name ?? '')))
    .replace('--- Other info for item. ---', escapeXml(row.alt_name == null ? '' : String(row.alt_name)))
    .replace('--- Price: 1,000.00 ---', escapeXml(`${t('field.price')}: ${formatMoney(priceSource, settings.decimals)}`));
  const doc = new DOMParser().parseFromString(filled, 'image/svg+xml');
  const root = doc.documentElement;
  if (root.nodeName !== 'svg') return { svg: filled, fallback: false };
  const target = doc.getElementById('replace-barcode');
  const { svg: barcode, fallback } = drawBarcode(String(row.code ?? ''), String(row.symbology ?? 'code128'));
  if (target) {
    while (target.firstChild) target.removeChild(target.firstChild);
    const width = Number(barcode.getAttribute('width')?.replace('px', '') ?? 0);
    const labelWidth = Number(root.getAttribute('width') ?? settings.width) || settings.width;
    const x = width && width < labelWidth ? Math.max(10, (labelWidth - width) / 2) : 10;
    target.setAttribute('transform', `matrix(1 0 0 1 ${x} ${settings.height - 60})`);
    const imported = doc.importNode(barcode, true);
    target.appendChild(imported);
  }
  return { svg: new XMLSerializer().serializeToString(root), fallback };
}

function printSvg(svg: string, width: number, height: number) {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.right = '0';
  frame.style.bottom = '0';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  document.body.appendChild(frame);
  const doc = frame.contentDocument;
  if (!doc) return;
  doc.open();
  doc.write(`<!doctype html><html><head><title>label</title><style>@page{size:${width}px ${height}px;margin:0}html,body{margin:0;padding:0}svg{display:block}</style></head><body>${svg}</body></html>`);
  doc.close();
  window.setTimeout(() => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    window.setTimeout(() => frame.remove(), 1000);
  }, 300);
}

export function ItemLabelPreview({ row }: { row: CrudRecord }) {
  const location = useSessionLocation();
  const [state, setState] = useState<{ svg: string; fallback: boolean; settings: LabelSettings } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const autoPrinted = useRef(false);
  const locationName = location.locations.find((entry) => entry.value === location.currentId)?.label ?? '';

  useEffect(() => {
    let cancelled = false;
    void ensureLocation().catch(() => undefined);
    loadLabelSettings()
      .then((settings) => {
        if (cancelled) return;
        const built = buildLabelSvg(row, settings, locationName);
        setState({ ...built, settings });
        if (settings.printDialog && !autoPrinted.current) {
          autoPrinted.current = true;
          printSvg(built.svg, settings.width, settings.height);
        }
      })
      .catch(() => !cancelled && setError(t('catalog.label.loadFailed')));
    return () => {
      cancelled = true;
    };
  }, [row, locationName]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!state) return <LinearProgress />;
  return (
    <Stack spacing={2}>
      {!state.settings.svg ? <Alert severity="info">{t('catalog.label.defaultTemplate')}</Alert> : null}
      {state.fallback ? <Alert severity="warning">{t('catalog.label.barcodeFallback')}</Alert> : null}
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1, '& svg': { maxWidth: '100%', height: 'auto' } }} dangerouslySetInnerHTML={{ __html: state.svg }} />
      <Alert severity="info">{t('catalog.label.instructions')}</Alert>
      <Button variant="contained" onClick={() => printSvg(state.svg, state.settings.width, state.settings.height)}>{t('catalog.label.print')}</Button>
    </Stack>
  );
}

/** Row action handler: open the label preview/print dialog. */
export function openItemLabel(row: CrudRecord, ctx: Pick<CrudActionContext, 'openDialog'>) {
  ctx.openDialog(() => <ItemLabelPreview row={row} />, { title: t('catalog.label.title', { name: String(row.name ?? '') }), maxWidth: 'sm' });
}
